import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, TrendingUp, Calendar, Users } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const PERIODS = [
  { key: 7, label: "7D" },
  { key: 14, label: "14D" },
  { key: 30, label: "30D" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

export default function LoginActivityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/login-activity?days=${days}`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Failed to load</p>;

  const { summary, chart_data } = data;

  return (
    <div className="space-y-4" data-testid="login-activity-panel">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-sky-50 rounded-xl p-3 text-center border border-sky-100">
          <p className="text-lg font-bold text-sky-700">{summary.total_logins}</p>
          <p className="text-[10px] text-sky-500 font-medium">Total Logins</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
          <p className="text-lg font-bold text-emerald-700">{summary.avg_daily_logins}</p>
          <p className="text-[10px] text-emerald-500 font-medium">Avg/Day</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-lg font-bold text-amber-700">{summary.peak_logins}</p>
          <p className="text-[10px] text-amber-500 font-medium">Peak Day</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setDays(p.key)}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
              days === p.key ? "bg-white text-slate-800 shadow-sm" : "text-slate-400 hover:text-slate-600"
            }`}
            data-testid={`login-period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chart_data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gLogins" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gUnique" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              tickFormatter={(v) => { const p = v.split("-"); return `${p[1]}/${p[2]}`; }}
              axisLine={false} tickLine={false}
            />
            <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="logins" name="Total Logins" stroke="#3b82f6" fill="url(#gLogins)" strokeWidth={2} />
            <Area type="monotone" dataKey="unique_users" name="Unique Users" stroke="#10b981" fill="url(#gUnique)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-5">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
          <span className="text-[10px] text-slate-500 font-medium">Total Logins</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span className="text-[10px] text-slate-500 font-medium">Unique Users</span>
        </div>
      </div>
    </div>
  );
}
